import "babel-polyfill";
import "vue2-animate/dist/vue2-animate.min.css";
import Vue from "vue";
import VueI18n from "vue-i18n";
import Nprogress from "nprogress";
import VeeValidate from "vee-validate";
import Notifications from "vue-notification";
import velocity from "velocity-animate";
import moment from "moment";
import VueMatchHeights from "vue-match-heights";
import App from "./App.vue";
import { store } from "./store/store";
import router from "./router/index.js";
import GlobalComponents from "./globalComponents";
import GlobalFilters from "@/globalFilters";

import vuetify from "./plugins/vuetify";

import messages from "./locale";
import validationMessagesPl from "vee-validate/dist/locale/pl";
import validationMessagesEn from "vee-validate/dist/locale/en";

import VueScrollTo from "vue-scrollto";
import "leaflet/dist/leaflet.css";
import { vueGtmConfig } from "./plugins/vue-gtm";

import VueGtm from "vue-gtm";
import { permissionCheck } from "@/helpers/permission-check";
import { dev__warnNotify } from "@/helpers/helpers";
import { Permission } from "@/enums/permission";

import * as Sentry from "@sentry/vue";
import { Integrations } from "@sentry/tracing";
import posthog from "posthog-js";
import { moduleCheck } from "@/helpers/module-check";
import { FEATURE_FLAG } from "@/helpers/posthog";
import { AccountType } from "@/enums/account-type";
import blockEvents from "@/helpers/block-events-check";
import registerAnalyticsGroup from "@/helpers/register-analytics-group";
import usersService from "@/services/users";
import serviceHandler from "@/helpers/handlers/service";
import { setCustomValidators } from "@/config/validators";

import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";
import mixpanel from "mixpanel-browser";
import { LOCAL_STORAGE_KEYS } from "@/enums/local-storage";
import { makeServer } from "../mock/server";

if (process.env.NODE_ENV === "development") {
  // makeServer();
}
// Near entry of your product, init Mixpanel
mixpanel.init(process.env.VUE_APP_MIXPANEL_APP_ID, {
  // persistence: "localStorage"
});

const setLanguage = async () => {
  if (!store.getters["auth/isAuthenticated"]) {
    return;
  }
  const { language, company_subscription } = await serviceHandler(
    usersService.getUserDetails()
  );
  store.dispatch("auth/setPlanRestrictions", company_subscription);
  store.dispatch("settings/setLanguage", language);
};

const checkWizardCompleted = async next => {
  if (!store.getters["company/isWizardCompleted"]) {
    next({ name: "wizard" });
    return;
  }
};

const fetchCompanies = async () => {
  store.commit("company/SET_LOADING", true);
  await store.dispatch("company/fetchCompanies");
  store.commit("company/SET_LOADING", false);
};

const setAnalytics = async (to, from, next) => {
  document.cookie = "__ca__chat" + "=; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
  document.cookie = "_ueuuid" + "=; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
  if (!store.state.company.company) {
    next();
    return;
  }
  const { user_id } = store.state.company.company.company_user;
  Sentry.setUser({ id: user_id });

  if (blockEvents()) {
    next();
    return;
  }
  const { id: company_id } = store.state.company.company;
  mixpanel.alias(user_id, mixpanel.get_distinct_id());
  mixpanel.identify(user_id);
  mixpanel.people.set({
    company_id: company_id,
    $name: store.state.auth.user.email,
    $email: store.state.auth.user.email
  });

  registerAnalyticsGroup(store.state.company);

  next();
  return;
};

Vue.config.productionTip = false;

Vue.prototype.$permission = Permission;
Vue.prototype.$hasAccess = permission => permissionCheck(permission);
Vue.prototype.$setUserProperties = params => {
  if (blockEvents()) return;
  if (!window.gtag) return;
  window.gtag("set", "user_properties", params);
};
router.beforeEach(async (to, from, next) => {
  const isAuthenticated = store.getters["auth/isAuthenticated"];
  Nprogress.start();

  posthog.reloadFeatureFlags();
  posthog.onFeatureFlags(flags => {
    const route = "maintenance-mode";
    if (flags.includes(FEATURE_FLAG.SERVICE_UNAVAILABLE) && to.name !== route) {
      next({ name: route });
    }
  });

  if (to.query.lang) {
    const lang = store.state.settings.languages.find(
      lang => lang.locale === to.query.lang
    );
    if (lang) {
      store.dispatch("settings/changeLanguage", lang);
    }
  }

  if (
    store.state.company.companies.length ||
    !to.meta.requiresAuth ||
    to.matched[0].path === "/wizard"
  ) {
    next();
    return;
  }

  if (!to.meta.requiresAuth && isAuthenticated) {
    next({
      name: "dashboard"
    });
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      next({
        name: "session-login",
        params: { redirectAfterLoginPath: to.fullPath }
      });
      return;
    }
  }

  await fetchCompanies();
  if (to.redirectedFrom !== "/wizard") {
    await checkWizardCompleted(next);
    await setLanguage();
    await setAnalytics(to, from, next);
  }
  next();
});

router.beforeResolve((to, from, next) => {
  if (to.meta.hasOwnProperty("module") && !moduleCheck(to.meta.module)) {
    next({ name: "dashboard" });
  } else {
    next();
  }
});

router.afterEach((to, from) => {
  Nprogress.done();
  if (!store.getters["company/isWizardCompleted"]) return;
  if (
    to.matched.some(record => record.meta.requiresAuth) &&
    !store.state.externalChanges.summary
  ) {
    if (
      (store.getters["company/accountType"] === AccountType.OWNER ||
        store.getters["company/accountType"] === AccountType.ADMIN) &&
      planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA)
    ) {
      store.dispatch("externalChanges/fetchProtectionSummary");
    }
  }
});

Vue.use(GlobalComponents);
Vue.use(GlobalFilters);
Vue.use(Notifications, { velocity });
Vue.use(VueI18n);
Vue.use(VueScrollTo);
Vue.use(VueMatchHeights, {
  disabled: [768]
});
Vue.use(VueGtm, vueGtmConfig);

// custom method registration to omit old (GA Universal) keys added to event (like "category", "action", etc.)
Vue.gtm.trackCustomEvent = params => {
  const { id: company_id, parent: parent_company_id } = store.state.company
    .company || {
    id: null,
    parent: null
  };

  try {
    if (blockEvents()) return;
    let is_paying = null;
    let is_testing = null;
    let is_agency = null;
    let is_free_trial = null;
    let free_trial_end_date = null;
    let free_trial_plan_name = null;
    let company_subscription_type;
    let company_plan;
    let company_acquisition_campaign;

    if (store.state.company.company) {
      is_paying = store.state.company.parent
        ? store.state.company.parent.is_paying
        : store.state.company.company.is_paying;

      is_testing = store.state.company.parent
        ? store.state.company.parent.is_testing
        : store.state.company.company.is_testing;

      is_agency = store.state.company.parent
        ? store.state.company.parent.is_agency
        : store.state.company.company.is_agency;

      is_free_trial = store.getters["auth/isFreeTrial"];
      if (is_free_trial) {
        free_trial_plan_name = store.getters["auth/planName"];
        free_trial_end_date = store.getters["auth/planExpiresAt"];
      }
      const isSubscriptionPlanClient =
        store.getters["company/isSubscriptionPlanClient"];

      company_subscription_type = isSubscriptionPlanClient
        ? "automatic_subscription"
        : "manual_subscription";

      const subsriptionName = store.getters["auth/planName"];

      company_plan = !isSubscriptionPlanClient
        ? "Other"
        : subsriptionName || "No active subscription";

      company_acquisition_campaign =
        store.getters["company/acquisitionCampaign"];
    }

    if (!("event" in params)) throw new Error("[GTM] No event name provided.");

    const eventName = params.event;
    delete params.event;
    const demoUser = localStorage.getItem(LOCAL_STORAGE_KEYS.DEMO_USER);

    const eventData = {
      page_url: router.currentRoute.path,
      page_name: router.currentRoute.name,
      ...(demoUser && { demo_user: demoUser }),
      ...(company_id && { company_id }),
      ...((parent_company_id || company_id) && {
        parent_company_id: parent_company_id || company_id
      }),
      ...params,
      is_paying,
      is_testing,
      is_agency,
      is_free_trial,
      free_trial_plan_name,
      free_trial_end_date,
      company_subscription_type,
      company_plan,
      company_acquisition_campaign
    };

    mixpanel.track(eventName, eventData);

    if ("Intercom" in window) {
      window.Intercom("trackEvent", eventName, eventData);
    }
  } catch (error) {
    dev__warnNotify(error);
  }
};

const currentLang = store.state.settings.selectedLocale;
export const i18n = new VueI18n({
  messages,
  locale: currentLang
});

moment.locale(currentLang);
Vue.use(VeeValidate, {
  i18n,
  dictionary: {
    pl: validationMessagesPl,
    en: validationMessagesEn
  }
});

Sentry.init({
  Vue: Vue,
  integrations: [new Integrations.BrowserTracing()],
  tracingOptions: {
    trackComponents: true
  },
  tracesSampleRate: 1.0,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  environment: process.env.VUE_APP_SENTRY_ENVIRONMENT || "dev"
});

posthog.init(process.env.VUE_APP_POSTHOG_API_KEY || "", {
  api_host: "https://app.posthog.com",
  autocapture: false
});

const app = new Vue({
  store,
  i18n,
  router,
  vuetify,
  render: h => h(App),
  components: { App }
}).$mount("#app");

if (window.Cypress) {
  // only available during E2E tests
  window.app = app;
}

setCustomValidators(i18n);
